<template>
  <component
    :is="isLink ? 'a' : 'button'"
    :href="href"
    v-tippy="{
      content: btnDescription,
      theme: 'light-border',
      interactive: true,
      placement: btnDescriptionPlacement,
    }"
    class="
      inline-flex
      items-center
      justify-center
      select-none
      border border-transparent
      text-sm
      font-medium
      focus:outline-none
      focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
      leading-none
      relative
    "
    :class="classes"
  >
    <span
      v-if="loading"
      class="absolute left-0 top-0 bg-opacity-80 items-center justify-center flex w-full h-full"
      :class="{ ...colorClasses, ...shapeClasses }"
    >
      <div class="w-5 h-5 border-white border-2 rounded-full border-t-0 animate-spin"></div>
    </span>
    <component :is="icon" class="h-5 w-5"></component>
    <slot></slot>
    <div
      class="
        absolute
        bg-vice-light-red
        rounded-full
        flex
        justify-center
        items-center
        z-20
        w-5
        h-5
        -top-1
        -right-1
      "
      v-if="showNotification"
    >
      <div class="rounded-full bg-white w-2 h-2"></div>
    </div>
  </component>
</template>

<script>
import * as icons from '@heroicons/vue/solid';
import { directive } from 'vue-tippy';

import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';

export default {
  components: {
    ...icons,
  },
  directives: {
    tippy: directive,
  },
  props: {
    href: { type: String, required: false },
    icon: { type: String },
    small: { type: Boolean, default: false },
    medium: { type: Boolean, default: false },
    normal: { type: Boolean, default: false },
    mediumRed: { type: Boolean, default: false },
    red: { type: Boolean, default: false },
    rose: { type: Boolean, default: false },
    dark: { type: Boolean, default: false },
    white: { type: Boolean, default: false },
    whiteTeal: { type: Boolean, default: false },
    indigo: { type: Boolean, default: false },
    indigoLight: { type: Boolean, default: false },
    indigoDark: { type: Boolean, default: false },
    yellow: { type: Boolean, default: false },
    orange: { type: Boolean, default: false },
    grey: { type: Boolean, default: false },
    circle: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    btnDescription: { type: String, default: '' },
    btnDescriptionPlacement: { type: String, default: 'top' },
    showNotification: { type: Boolean, default: false },
  },
  computed: {
    isLink() {
      return !!this.href;
    },
    hasText() {
      return !!this.$slots.default;
    },
    classes() {
      return {
        ...this.shapeClasses,
        ...this.stateClasses,
        ...this.colorClasses,
      };
    },
    shapeClasses() {
      return {
        'rounded-md': !this.circle,
        'rounded-full': this.circle,
        'gap-x-1 px-2 h-8': this.small,
        'gap-x-1 px-2 h-9': this.medium,
        'gap-x-2 px-3 h-11': !this.small && !this.medium && !this.normal,
        'w-11': this.circle && !this.hasText && !this.small,
        'w-8': this.circle && !this.hasText && this.small,
      };
    },
    stateClasses() {
      return {
        'pointer-events-none opacity-60 cursor-not-allowed': this.disabled,
      };
    },
    colorClasses() {
      const conditionsMet = [
        this.red,
        this.rose,
        this.dark,
        this.white,
        this.grey,
        this.indigo,
        this.yellow,
        this.orange,
        this.whiteTeal,
        this.mediumRed,
        this.indigoDark,
        this.indigoLight,
      ].some((cond) => cond);
      return {
        'bg-transparent hover:bg-red-400 text-red-500 hover:text-white border-red-500 hover:border-transparent':
          this.rose,
        'bg-vice-rose-400 hover:bg-vice-rose-500 text-white': this.red,
        'bg-vice-rose-600 hover:bg-vice-rose-700 text-white': this.mediumRed,
        'bg-indigo-400 hover:bg-indigo-500 text-white': this.indigo,
        'bg-indigo-100 hover:bg-indigo-200 text-indigo-800': this.indigoLight,
        'bg-indigo-800 hover:bg-indigo-900 text-white': this.indigoDark,
        'bg-vice-light-yellow hover:bg-vice-light-yellow-hover text-vice-dark-yellow': this.yellow,
        'bg-vice-orange hover:bg-vice-light-yellow-hover text-vice-dark-yellow': this.orange,
        'bg-vice-teal-600 hover:bg-vice-teal-700 text-white': this.dark,
        'bg-white hover:bg-gray-100 text-vice-teal-500': this.whiteTeal,
        'bg-vice-teal-500 hover:bg-vice-teal-700 text-white': !conditionsMet,
        'bg-white hover:bg-gray-100 text-vice-dark-grey border-vice-dark-grey': this.white,
        'bg-vice-light-grey': this.grey,
      };
    },
  },
};
</script>
